<template>
  <CCard class="company-wide-notification-card">
    <CCardHeader class="company-wide-header">
      <div>
        <p class="company-wide-kicker">COMPANY-WIDE PROJECT CHANNELS</p>
        <h2>会社全体通知設定</h2>
        <p>
          SlackチャンネルIDが登録され、有効な所属メンバーが1人以上いるプロジェクトへ、
          会社共通の内容を自動送信します。プロジェクトを一覧から選ぶ必要はありません。
        </p>
      </div>
      <div class="company-wide-summary">
        <CBadge :color="eligibleProjectCount > 0 ? 'info' : 'warning'">
          対象 {{ eligibleProjectCount }}プロジェクト
        </CBadge>
        <CBadge :color="destinationChannelCount > 0 ? 'info' : 'warning'">
          {{ destinationChannelCount }}チャンネル
        </CBadge>
        <CBadge :color="enabledScheduleCount > 0 ? 'success' : 'secondary'">
          {{ enabledScheduleCount > 0 ? `${enabledScheduleCount}種類ON` : 'すべてOFF' }}
        </CBadge>
      </div>
    </CCardHeader>

    <CCardBody>
      <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
        {{ errorMessage }}
      </CAlert>
      <CAlert v-if="successMessage" color="success" close-button @update:show="successMessage = ''">
        {{ successMessage }}
      </CAlert>

      <div v-if="loading" class="company-wide-loading">
        <CSpinner color="primary"/>
        <span>会社全体通知設定を読み込んでいます。</span>
      </div>

      <template v-else>
        <CAlert v-if="!slackFeatureEnabled" color="warning" class="company-availability-alert">
          現在の契約プランではSlack通知を利用できません。
        </CAlert>
        <CAlert v-else-if="!slackTokenConfigured" color="warning" class="company-availability-alert">
          自社設定にSlack Botトークンがありません。先に自社設定で登録してください。
        </CAlert>

        <CAlert color="info">
          送信先は、各プロジェクト更新画面の「共通SlackチャンネルID」から自動判定します。
          休暇通知やプロジェクト個別定期通知がOFFでも、チャンネルIDと所属メンバーがあれば対象です。
          同じチャンネルIDを複数プロジェクトで共有している場合は1回だけ送信します。
        </CAlert>
        <CAlert color="secondary">
          プロジェクトごとの週初・週末・月末コメントは廃止していません。
          個別通知は各プロジェクト更新画面の設定を使用し、会社全体通知とは別に送信されます。
        </CAlert>
        <CAlert v-if="slackAvailable && eligibleProjectCount === 0" color="warning">
          送信対象がありません。プロジェクト更新画面で共通SlackチャンネルIDを登録し、
          有効な所属メンバーを1人以上設定してください。
        </CAlert>

        <section class="company-test-target" aria-labelledby="company-test-target-heading">
          <div>
            <p class="company-wide-kicker">TEST DESTINATION</p>
            <h3 id="company-test-target-heading">テスト送信先</h3>
            <p>保存前の内容を、対象プロジェクトのうち1チャンネルだけへ確認送信できます。</p>
          </div>
          <div class="company-test-target__field">
            <label for="companyWideTestProject">プロジェクト</label>
            <select
              id="companyWideTestProject"
              v-model="selectedTestProjectId"
              class="custom-select"
              :disabled="processing || !slackAvailable || eligibleProjects.length === 0"
            >
              <option value="">
                {{ eligibleProjects.length ? 'テスト送信先を選択' : 'テスト可能な送信先なし' }}
              </option>
              <option
                v-for="project in eligibleProjects"
                :key="project.project_id"
                :value="String(project.project_id)"
              >
                {{ project.project_name }}（{{ project.slack_channel }}）
              </option>
            </select>
            <small>有効なチャンネルIDと所属メンバーがあるプロジェクトだけ表示します。</small>
          </div>
        </section>

        <CAlert color="secondary" class="company-mention-guide">
          <strong>メンション：</strong>
          <code>@channel</code>、<code>@here</code>、<code>@everyone</code>と、
          正しいSlackメンバーIDが登録された会社利用者を本文へ挿入できます。
        </CAlert>

        <section
          v-for="group in scheduleGroups"
          :key="group.key"
          class="company-schedule-group"
        >
          <div class="company-schedule-group__heading">
            <h3>{{ group.label }}</h3>
            <p>{{ group.description }}</p>
          </div>
          <div class="company-schedule-grid">
          <article
            v-for="schedule in group.items"
            :key="schedule.code"
            class="company-schedule-card"
            :class="{ 'company-schedule-card--enabled': form[schedule.flagField] }"
          >
            <header class="company-schedule-card__header">
              <div>
                <p class="company-wide-kicker">{{ schedule.kicker }}</p>
                <h3>{{ schedule.label }}</h3>
                <p>{{ schedule.description }}</p>
              </div>
              <span
                class="company-schedule-status"
                :class="form[schedule.flagField]
                  ? 'company-schedule-status--on'
                  : 'company-schedule-status--off'"
                role="status"
              >
                {{ form[schedule.flagField] ? '通知ON' : '通知OFF' }}
              </span>
            </header>

            <div class="company-schedule-controls">
              <button
                type="button"
                class="company-stable-switch"
                :class="{ 'company-stable-switch--on': form[schedule.flagField] }"
                role="switch"
                :aria-checked="form[schedule.flagField] ? 'true' : 'false'"
                :disabled="processing || (!slackAvailable && !form[schedule.flagField])"
                @mousedown.prevent
                @click.prevent.stop="toggleSchedule(schedule)"
              >
                <span class="company-stable-switch__track" aria-hidden="true">
                  <span class="company-stable-switch__thumb"></span>
                </span>
                <span>
                  <strong>{{ form[schedule.flagField] ? '通知ON' : '通知OFF' }}</strong>
                  <small>このタイミングだけ切り替えます</small>
                </span>
              </button>

              <div class="company-time-field">
                <label :for="`company-wide-time-${schedule.code}`">送信時刻</label>
                <input
                  :id="`company-wide-time-${schedule.code}`"
                  v-model="form[schedule.timeField]"
                  class="form-control"
                  type="time"
                  step="60"
                  :disabled="processing"
                  @input="clearScheduleError(schedule.code)"
                >
              </div>
            </div>

            <label :for="`company-wide-message-${schedule.code}`">会社全体へ送る内容</label>
            <div class="company-mention-toolbar">
              <span>挿入</span>
              <CButton
                v-for="mention in publicMentions"
                :key="`${schedule.code}-${mention.label}`"
                type="button"
                size="sm"
                color="secondary"
                variant="outline"
                :disabled="processing"
                @click="appendMention(schedule, mention.syntax)"
              >
                {{ mention.label }}
              </CButton>
              <select
                class="custom-select company-user-mention-select"
                :disabled="processing || mentionableUsers.length === 0"
                @change="appendSelectedUser(schedule, $event)"
              >
                <option value="">
                  {{ mentionableUsers.length ? '会社利用者を選択' : '個人メンション候補なし' }}
                </option>
                <option
                  v-for="user in mentionableUsers"
                  :key="`${schedule.code}-${user.user_id}`"
                  :value="user.slack_id"
                >
                  {{ user.name }}（{{ user.slack_id }}）
                </option>
              </select>
            </div>

            <textarea
              :id="`company-wide-message-${schedule.code}`"
              v-model="form[schedule.messageField]"
              class="form-control company-message"
              rows="6"
              maxlength="1000"
              :disabled="processing"
              :placeholder="schedule.placeholder"
              @input="clearScheduleError(schedule.code)"
            ></textarea>
            <div class="company-message-meta">
              <p v-if="scheduleErrors[schedule.code]" class="text-danger" role="alert">
                {{ scheduleErrors[schedule.code] }}
              </p>
              <small>{{ messageLength(schedule) }}/1000文字</small>
            </div>

            <CButton
              type="button"
              color="secondary"
              variant="outline"
              :disabled="processing || !slackAvailable || !selectedTestProjectId"
              @click="sendTest(schedule)"
            >
              {{ testingCode === schedule.code ? '送信しています' : 'この内容を選択先へテスト送信' }}
            </CButton>
          </article>
          </div>
        </section>

        <div class="company-wide-actions">
          <CButton
            type="button"
            color="primary"
            :disabled="processing || (!slackAvailable && enabledScheduleCount > 0)"
            @click="saveSettings"
          >
            {{ saving ? '保存しています' : '会社全体通知設定を保存' }}
          </CButton>
        </div>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import api from '@/services/projectNotificationCompanySettings'

const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/
const CHANNEL_PATTERN = /^[CG][A-Z0-9]{8,30}$/
const MEMBER_PATTERN = /^[UW][A-Z0-9]{8,30}$/

// サーバがまだ配列を読まない期間、この3種だけは従来の項目名でも送る。
// 新しいタイミングはここへ足さない（配列で受け取ってもらう）。
const LEGACY_SCHEDULE_CODES = ['WEEK_START', 'WEEK_END', 'MONTH_END']

// 月次と週次を混ぜて並べると読み違えて設定しやすい。見出しで分けて出す。
const SCHEDULE_GROUPS = [
  {
    key: 'MONTHLY',
    label: '月次',
    description: '月の始まりと終わりに送ります。月初は提出の開始、月末は締めの案内に使います。'
  },
  {
    key: 'WEEKLY',
    label: '週次',
    description: '週の始まりと終わりに送ります。週初は重点の共有、週最終は実績と翌週予定の共有に使います。'
  }
]

export default {
  name: 'CompanyWideProjectNotificationSettings',
  data () {
    return {
      schedules: [
        {
          code: 'MONTH_START',
          group: 'MONTHLY',
          kicker: 'MONTH START',
          label: '月初営業日',
          description: '毎月の最初の会社営業日に送信します。',
          placeholder: '例：先月分の勤務表・経費の提出をお願いします。',
          flagField: 'month_start_flg',
          timeField: 'month_start_time',
          messageField: 'month_start_message',
          defaultTime: '09:10'
        },
        {
          code: 'WEEK_START',
          group: 'WEEKLY',
          kicker: 'WEEK START',
          label: '週初営業日',
          description: '毎週の最初の会社営業日に送信します。',
          placeholder: '例：今週の全社重点事項と確認期限を共有してください。',
          flagField: 'week_start_flg',
          timeField: 'week_start_time',
          messageField: 'week_start_message',
          defaultTime: '09:05'
        },
        {
          code: 'WEEK_END',
          group: 'WEEKLY',
          kicker: 'WEEK END',
          label: '週最終営業日',
          description: '毎週の最後の会社営業日に送信します。',
          placeholder: '例：今週の実績・課題・翌週予定を共有してください。',
          flagField: 'week_end_flg',
          timeField: 'week_end_time',
          messageField: 'week_end_message',
          defaultTime: '17:25'
        },
        {
          code: 'MONTH_END',
          group: 'MONTHLY',
          kicker: 'MONTH END',
          label: '月末最終営業日',
          description: '毎月の最後の会社営業日に送信します。',
          placeholder: '例：勤怠・業務報告・請求関連の月末確認をお願いします。',
          flagField: 'month_end_flg',
          timeField: 'month_end_time',
          messageField: 'month_end_message',
          defaultTime: '17:35'
        }
      ],
      publicMentions: [
        { label: '@channel', syntax: '<!channel>' },
        { label: '@here', syntax: '<!here>' },
        { label: '@everyone', syntax: '<!everyone>' }
      ],
      loading: true,
      saving: false,
      testingCode: '',
      errorMessage: '',
      successMessage: '',
      scheduleErrors: {},
      slackFeatureEnabled: false,
      slackTokenConfigured: false,
      eligibleProjectCount: 0,
      destinationChannelCount: 0,
      eligibleProjects: [],
      mentionUsers: [],
      selectedTestProjectId: '',
      // タイミングを増やしても触らずに済むよう、schedules から作る。
      form: {}
    }
  },
  computed: {
    // 月次と週次に分けて出す。各グループ内は 始まり → 終わり の順。
    scheduleGroups () {
      return SCHEDULE_GROUPS
        .map(group => ({
          ...group,
          items: this.schedules.filter(schedule => schedule.group === group.key)
        }))
        .filter(group => group.items.length)
    },
    processing () {
      return this.saving || Boolean(this.testingCode)
    },
    slackAvailable () {
      return this.slackFeatureEnabled && this.slackTokenConfigured
    },
    enabledScheduleCount () {
      return this.schedules.filter(schedule => this.form[schedule.flagField]).length
    },
    mentionableUsers () {
      return this.mentionUsers.filter(user => user.mention_available && MEMBER_PATTERN.test(user.slack_id))
    }
  },
  mounted () {
    this.loadAll()
  },
  methods: {
    async loadAll () {
      this.loading = true
      this.errorMessage = ''
      try {
        const [settings, projects] = await Promise.all([
          api.getSettings(),
          api.list(false)
        ])
        this.applySettings(settings)
        this.eligibleProjects = Array.isArray(projects)
          ? projects
            .map(this.normalizeProject)
            .filter(project => project.project_id && CHANNEL_PATTERN.test(project.slack_channel) && project.active_member_count > 0)
            .sort((first, second) => first.project_name.localeCompare(second.project_name, 'ja'))
          : []
        if (!this.eligibleProjects.some(project => String(project.project_id) === String(this.selectedTestProjectId))) {
          this.selectedTestProjectId = this.eligibleProjects.length ? String(this.eligibleProjects[0].project_id) : ''
        }
      } catch (error) {
        this.errorMessage = this.apiError(error, '会社全体通知設定を読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },
    applySettings (value = {}) {
      this.slackFeatureEnabled = value.slack_feature_enabled === true
      this.slackTokenConfigured = value.slack_token_configured === true
      this.eligibleProjectCount = this.nonNegativeInteger(value.enabled_project_count)
      this.destinationChannelCount = this.nonNegativeInteger(value.destination_channel_count)
      this.mentionUsers = Array.isArray(value.mention_users)
        ? value.mention_users.map(this.normalizeUser)
        : []
      // 応答に schedules 配列があればそれを採用。無ければ従来の項目名で読む。
      const rows = Array.isArray(value.schedules) ? value.schedules : []
      this.schedules.forEach(schedule => {
        const row = rows.find(item => item && item.schedule_code === schedule.code)
        if (row) {
          this.$set(this.form, schedule.flagField, row.notification_flg === true)
          this.$set(this.form, schedule.timeField, this.normalizeTime(row.notification_time, schedule.defaultTime))
          this.$set(this.form, schedule.messageField, String(row.message || ''))
          return
        }
        this.$set(this.form, schedule.flagField, value[schedule.flagField] === true)
        this.$set(this.form, schedule.timeField, this.normalizeTime(value[schedule.timeField], schedule.defaultTime))
        this.$set(this.form, schedule.messageField, String(value[schedule.messageField] || ''))
      })
    },
    normalizeProject (value = {}) {
      return {
        project_id: this.positiveInteger(value.project_id),
        project_name: String(value.project_name || '名称未設定プロジェクト').trim() || '名称未設定プロジェクト',
        slack_channel: String(value.slack_channel || '').trim().toUpperCase(),
        active_member_count: this.nonNegativeInteger(value.active_member_count)
      }
    },
    normalizeUser (value = {}) {
      const slackId = String(value.slack_id || '').trim().toUpperCase()
      return {
        user_id: value.user_id,
        name: String(value.name || '').trim() || '氏名未設定',
        slack_id: slackId,
        mention_available: value.mention_available === true && MEMBER_PATTERN.test(slackId)
      }
    },
    toggleSchedule (schedule) {
      if (!this.form[schedule.flagField] && !this.slackAvailable) return
      const scrollX = window.pageXOffset
      const scrollY = window.pageYOffset
      this.$set(this.form, schedule.flagField, !this.form[schedule.flagField])
      this.clearScheduleError(schedule.code)
      this.$nextTick(() => window.scrollTo(scrollX, scrollY))
    },
    appendMention (schedule, syntax) {
      const field = schedule.messageField
      const current = String(this.form[field] || '')
      this.$set(this.form, field, current ? `${current} ${syntax}` : syntax)
      this.clearScheduleError(schedule.code)
    },
    appendSelectedUser (schedule, event) {
      const slackId = String(event.target.value || '')
      if (MEMBER_PATTERN.test(slackId)) this.appendMention(schedule, `<@${slackId}>`)
      event.target.value = ''
    },
    validateSchedule (schedule, requireMessage) {
      const time = String(this.form[schedule.timeField] || '').trim()
      const message = String(this.form[schedule.messageField] || '').trim()
      if (!TIME_PATTERN.test(time)) {
        this.$set(this.scheduleErrors, schedule.code, '送信時刻を00:00から23:59までの範囲で入力してください。')
        return false
      }
      if (requireMessage && !message) {
        this.$set(this.scheduleErrors, schedule.code, '会社全体へ送る内容を入力してください。')
        return false
      }
      if (message.length > 1000) {
        this.$set(this.scheduleErrors, schedule.code, '通知内容は1000文字以内で入力してください。')
        return false
      }
      return true
    },
    validateAll () {
      this.errorMessage = ''
      this.successMessage = ''
      let valid = true
      this.schedules.forEach(schedule => {
        if (!this.validateSchedule(schedule, this.form[schedule.flagField])) valid = false
      })
      if (this.enabledScheduleCount > 0 && !this.slackAvailable) valid = false
      if (this.slackAvailable && this.enabledScheduleCount > 0 && this.destinationChannelCount === 0) {
        this.errorMessage = '有効な送信先チャンネルがありません。プロジェクトの共通SlackチャンネルIDと所属メンバーを確認してください。'
        valid = false
      }
      return valid
    },
    /**
     * 保存する内容。
     *
     * <p>タイミングは schedules の配列で送る。増やしてもここを触らない。
     * 旧3種を項目名でも送るのは、サーバがまだ配列を読まない期間の互換
     * （新しいタイミングは配列にしか載らない）。</p>
     */
    payload () {
      const payload = {
        schedules: this.schedules.map(schedule => ({
          schedule_code: schedule.code,
          notification_flg: Boolean(this.form[schedule.flagField]),
          notification_time: String(this.form[schedule.timeField] || '').trim(),
          message: String(this.form[schedule.messageField] || '').trim() || null
        }))
      }
      LEGACY_SCHEDULE_CODES.forEach(code => {
        const schedule = this.schedules.find(item => item.code === code)
        if (!schedule) return
        payload[schedule.flagField] = Boolean(this.form[schedule.flagField])
        payload[schedule.timeField] = String(this.form[schedule.timeField] || '').trim()
        payload[schedule.messageField] = String(this.form[schedule.messageField] || '').trim() || null
      })
      return payload
    },
    async saveSettings () {
      if (!this.validateAll()) return
      this.saving = true
      try {
        this.applySettings(await api.updateSettings(this.payload()))
        this.successMessage = '会社全体通知設定を保存しました。送信先はチャンネルIDと所属メンバーから自動判定されます。'
        await this.loadEligibleProjectsOnly()
        this.$emit('updated')
      } catch (error) {
        this.errorMessage = this.apiError(error, '会社全体通知設定を保存できませんでした。')
      } finally {
        this.saving = false
      }
    },
    async loadEligibleProjectsOnly () {
      try {
        const projects = await api.list(false)
        this.eligibleProjects = Array.isArray(projects)
          ? projects.map(this.normalizeProject).filter(project => project.project_id && CHANNEL_PATTERN.test(project.slack_channel) && project.active_member_count > 0)
          : []
      } catch (error) {
        this.eligibleProjects = []
      }
    },
    async sendTest (schedule) {
      this.errorMessage = ''
      this.successMessage = ''
      if (!this.slackAvailable || !this.validateSchedule(schedule, true)) return
      const projectId = Number(this.selectedTestProjectId)
      if (!Number.isSafeInteger(projectId) || projectId <= 0) {
        this.errorMessage = 'テスト送信先を選択してください。'
        return
      }
      this.testingCode = schedule.code
      try {
        await api.test(schedule.code, projectId, {
          notification_time: String(this.form[schedule.timeField] || '').trim(),
          message: String(this.form[schedule.messageField] || '').trim()
        })
        this.successMessage = `${schedule.label}の内容をテスト送信しました。`
      } catch (error) {
        this.errorMessage = this.apiError(error, 'テスト送信できませんでした。')
      } finally {
        this.testingCode = ''
      }
    },
    clearScheduleError (code) {
      if (this.scheduleErrors[code]) this.$delete(this.scheduleErrors, code)
      this.errorMessage = ''
      this.successMessage = ''
    },
    messageLength (schedule) {
      return String(this.form[schedule.messageField] || '').length
    },
    normalizeTime (value, fallback) {
      const normalized = String(value || '').trim()
      return TIME_PATTERN.test(normalized) ? normalized : fallback
    },
    positiveInteger (value) {
      const number = Number(value)
      return Number.isSafeInteger(number) && number > 0 ? number : null
    },
    nonNegativeInteger (value) {
      const number = Number(value)
      return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0
    },
    apiError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data === 'object') return data.message || fallback
      if (typeof data === 'string' && data.trim()) return data
      // axiosが付ける英語のmessage（Network Error など）は画面へ出さない。
      // 画面側が自分で投げたErrorの日本語だけを通す。
      if (error && !error.isAxiosError && error.message) return error.message
      return fallback
    }
  }
}
</script>

<style scoped>
.company-wide-notification-card {
  border: 0;
  box-shadow: 0 18px 44px rgba(15, 53, 78, .09);
}
.company-wide-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}
.company-wide-header h2 { margin: 0 0 8px; }
.company-wide-header p { margin: 0; line-height: 1.7; }
.company-wide-kicker {
  margin: 0 0 5px !important;
  color: #0a6ca8;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .12em;
}
.company-wide-summary {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.company-wide-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 180px;
}
.company-test-target {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 420px);
  gap: 24px;
  align-items: end;
  margin: 22px 0;
  padding: 20px;
  border: 1px solid #d6e5ef;
  border-radius: 14px;
  background: #f7fbfe;
}
.company-test-target h3 { margin: 0 0 6px; }
.company-test-target p { margin: 0; }
.company-test-target__field { display: grid; gap: 6px; }
.company-schedule-group { margin-top: 22px; }
.company-schedule-group__heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #dce8ef;
  margin-bottom: 14px;
}
.company-schedule-group__heading h3 { margin: 0; color: #17384d; font-size: 16px; }
.company-schedule-group__heading p { margin: 0; color: #6a7f8c; font-size: 12px; }
/* 1グループ2枚。件数が増えても余りが出ないよう2列で折り返す。 */
.company-schedule-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 22px;
}
.company-schedule-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border: 1px solid #d8e3ea;
  border-radius: 16px;
  background: #fff;
}
.company-schedule-card--enabled {
  border-color: #35ad69;
  box-shadow: 0 10px 28px rgba(35, 154, 87, .1);
}
.company-schedule-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.company-schedule-card__header > div { min-width: 0; }
.company-schedule-card__header h3 { margin: 0 0 6px; }
.company-schedule-card__header p { margin: 0; line-height: 1.55; }
.company-schedule-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 72px;
  min-height: 28px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  word-break: keep-all;
  writing-mode: horizontal-tb;
  text-orientation: mixed;
}
.company-schedule-status--on {
  background: #dff5e7;
  color: #13733d;
}
.company-schedule-status--off {
  background: #e2e7ec;
  color: #536779;
}
.company-schedule-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px;
  gap: 12px;
  align-items: end;
}
.company-stable-switch {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  padding: 10px 14px;
  border: 1px solid #c7d5df;
  border-radius: 12px;
  background: #fff;
  color: #304b5f;
  text-align: left;
}
.company-stable-switch:disabled {
  cursor: not-allowed;
  opacity: .65;
}
.company-stable-switch--on {
  border-color: #27ae60;
  background: #effaf4;
}
.company-stable-switch__track {
  position: relative;
  display: inline-flex;
  flex: 0 0 54px;
  width: 54px;
  height: 30px;
  border-radius: 999px;
  background: #aebbc5;
  transition: background .15s ease;
}
.company-stable-switch--on .company-stable-switch__track { background: #27ae60; }
.company-stable-switch__thumb {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .2);
  transition: transform .15s ease;
}
.company-stable-switch--on .company-stable-switch__thumb { transform: translateX(24px); }
.company-stable-switch span:last-child {
  display: grid;
  min-width: 0;
}
.company-stable-switch strong { white-space: nowrap; }
.company-stable-switch small { color: #687f8f; }
.company-time-field { display: grid; gap: 5px; }
.company-mention-guide { margin-top: 18px; }
.company-mention-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
}
.company-user-mention-select { width: auto; min-width: 210px; }
.company-message { resize: vertical; }
.company-message-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  min-height: 22px;
}
.company-message-meta p { margin: 0; }
.company-wide-actions {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}
@media (max-width: 1199.98px) {
  .company-schedule-grid { grid-template-columns: 1fr; }
}
@media (max-width: 767.98px) {
  .company-wide-header,
  .company-test-target {
    display: grid;
    grid-template-columns: 1fr;
  }
  .company-wide-summary { justify-content: flex-start; }
  .company-schedule-controls { grid-template-columns: 1fr; }
  .company-user-mention-select { width: 100%; min-width: 0; }
}
</style>
